import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { NotificationPrefs } from "@/components/NotificationPrefs";
import {
  X, ArrowRight, ArrowLeft, Sparkles, PartyPopper, ChevronRight,
  ClipboardList, FileText, Megaphone, CalendarCheck, BarChart3, PenLine,
  Siren, CalendarDays, FileBarChart, Bell, CalendarClock,
} from "lucide-react";
import { V } from "@/lib/vocab";

// Copy + icon for each feature-step key the backend can return. Only enabled ones appear.
const featureCardsFactory = () => ({
  requests: { icon: ClipboardList, title: "Requests & work orders", desc: `Every maintenance issue ${V.a("person")} reports becomes a tracked ticket you can assign, prioritize and resolve.`, link: "/dashboard/actions" },
  documents: { icon: FileText, title: "AI Documents", desc: `Upload your bylaws, rules and policies — the concierge answers ${V.lcs("person")} with accurate, cited answers.`, link: "/dashboard/documents" },
  broadcasts: { icon: Megaphone, title: "Messages", desc: `Text or email everyone, a group, or one ${V.lc("person")} — now or scheduled — with delivery reports.`, link: "/dashboard/broadcasts" },
  amenities: { icon: CalendarCheck, title: "Reservations", desc: `Let ${V.lcs("person")} book the clubhouse, pool or gym — the AI handles requests and confirmations.`, link: "/dashboard/amenities" },
  surveys: { icon: BarChart3, title: "Surveys & votes", desc: `Collect ${V.lc("person")} feedback and run ${V.grp()} votes right through the concierge.`, link: "/dashboard/surveys" },
  signatures: { icon: PenLine, title: "E-Sign", desc: `Send any PDF for e-signature — ${V.lcs("board")} and ${V.lcs("person")} sign from their phone, reminders are automatic.`, link: "/dashboard/signatures" },
  emergency: { icon: Siren, title: "Emergency Alerts", desc: "Urgent issues escalate to your on-call ladder by text and call until someone responds.", link: "/dashboard/emergency" },
  events: { icon: CalendarDays, title: "Events", desc: `Publish ${V.lc("org")} events ${V.lcs("person")} can ask about and RSVP to.`, link: "/dashboard/events" },
  reports: { icon: FileBarChart, title: `${V.Grp()} & manager reports`, desc: `${V.AGrp()}-ready summary of calls, texts, resolutions and satisfaction — emailed on your schedule.`, link: "/dashboard/reports" },
});

const CADENCE = [
  ["weekly", "Weekly", "Every Monday morning"],
  ["monthly", "Monthly", "First of the month"],
  ["off", "Off", "Don't email a report"],
];

const stepMotion = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

function FeatureRow({ k, onGo, isNew }) {
  const FEATURE_CARDS = featureCardsFactory();
  const c = FEATURE_CARDS[k];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <div data-testid={`onboarding-feature-${k}`}
      className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-[#e7e1da] shadow-sm hover:shadow-md hover:border-[#e5342e]/30 transition-all group">
      <div className="p-3 rounded-xl bg-[#f7f4f0] text-[#e5342e] group-hover:bg-[#e5342e] group-hover:text-white transition-colors shrink-0">
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-heading font-bold text-[#191614] text-sm">{c.title}</h4>
          {isNew && <span className="text-[9px] font-black uppercase tracking-wider text-[#f59e0b] bg-[#f59e0b]/10 border border-[#f59e0b]/20 rounded-full px-1.5 py-0.5">New</span>}
        </div>
        <p className="text-sm text-[#191614]/60 mt-0.5 leading-snug">{c.desc}</p>
        <button data-testid={`onboarding-go-${k}`} onClick={() => onGo(c.link)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#e5342e] hover:gap-1.5 transition-all">
          Take me there <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function CadenceStep({ tenantId, initial }) {
  const [freq, setFreq] = useState(initial || "weekly");
  const [saving, setSaving] = useState(false);
  const choose = async (v) => {
    setFreq(v); setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}/report/schedule`, { frequency: v });
      toast.success(v === "off" ? "Report emails turned off" : `Report set to ${v}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="onboarding-cadence">
      {CADENCE.map(([v, label, sub]) => (
        <button key={v} data-testid={`cadence-${v}-card`} disabled={saving} onClick={() => choose(v)}
          className={`flex flex-col items-center justify-center p-5 rounded-2xl border-2 transition-all text-center disabled:opacity-60 ${
            freq === v ? "border-[#e5342e] bg-[#e5342e]/5 text-[#e5342e]" : "border-[#e7e1da] bg-white text-[#191614]/70 hover:border-[#e5342e]/30"}`}>
          <CalendarClock className="h-5 w-5 mb-2" strokeWidth={2} />
          <span className="font-heading font-bold text-sm">{label}</span>
          <span className="text-[11px] mt-0.5 opacity-70">{sub}</span>
        </button>
      ))}
    </div>
  );
}

export function OnboardingWizard({ tenantId, active, replayNonce, onReplayHandled }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [acking, setAcking] = useState(false);

  const fetchState = useCallback(async () => {
    if (!tenantId) return null;
    try {
      const r = await api.get(`/tenants/${tenantId}/onboarding/state`);
      return r.data;
    } catch { return null; }
  }, [tenantId]);

  // Auto-open on first login / when a new feature lights up.
  useEffect(() => {
    let cancelled = false;
    if (!active || !tenantId) { setOpen(false); setData(null); return; }
    fetchState().then((d) => {
      if (cancelled) return;
      setData(d);
      if (d?.should_show) { setIdx(0); setOpen(true); }
    });
    return () => { cancelled = true; };
  }, [active, tenantId, fetchState]);

  // Manual replay from the profile menu — reset seen state, then reopen the full tour.
  useEffect(() => {
    if (!replayNonce || !tenantId) return;
    let cancelled = false;
    (async () => {
      try { await api.post(`/tenants/${tenantId}/onboarding/reset`); } catch { /* ignore */ }
      const d = await fetchState();
      if (cancelled) return;
      if (d) { setData({ ...d, mode: "welcome" }); setIdx(0); setOpen(true); }
      onReplayHandled?.();
    })();
    return () => { cancelled = true; };
  }, [replayNonce]); // eslint-disable-line

  const steps = useMemo(() => {
    if (!data) return [];
    if (data.mode === "whats_new") {
      return [{ type: "whatsnew" }, { type: "done" }];
    }
    // welcome mode
    const out = [{ type: "welcome" }, { type: "features" }, { type: "notifications" }];
    if (data.can_configure) out.push({ type: "cadence" });
    out.push({ type: "done" });
    return out;
  }, [data]);

  const featuresToShow = data?.mode === "whats_new" ? (data?.new_features || []) : (data?.enabled_features || []);

  const ack = useCallback(async () => {
    if (!tenantId || !data) return;
    setAcking(true);
    try {
      await api.post(`/tenants/${tenantId}/onboarding/ack`, { features: featuresToShow });
    } catch { /* best effort */ }
    setAcking(false);
  }, [tenantId, data, featuresToShow]);

  const close = async () => { await ack(); setOpen(false); };
  const goTo = async (link) => { await ack(); setOpen(false); navigate(link); };

  if (!open || !data || !steps.length) return null;

  const step = steps[idx];
  const isLast = idx === steps.length - 1;
  const roleWord = data.seat_label ? data.seat_label.toLowerCase() : data.role === "board_member" ? `${V.lc("board")}` : `${V.lc("org")} manager`;

  const next = () => { if (isLast) close(); else setIdx((i) => Math.min(i + 1, steps.length - 1)); };
  const back = () => setIdx((i) => Math.max(i - 1, 0));

  return (
    <div data-testid="onboarding-wizard-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !acking) close(); }}
      className="fixed inset-0 z-[60] bg-[#191614]/60 backdrop-blur-md flex items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", damping: 26, stiffness: 320 }}
        className="relative w-full max-w-2xl bg-[#f7f4f0] rounded-3xl shadow-2xl border border-[#e7e1da] overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span key={i} className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-[#e5342e]" : i < idx ? "w-2 bg-[#e5342e]/40" : "w-2 bg-[#e7e1da]"}`} />
            ))}
          </div>
          <button data-testid="wizard-skip-button" onClick={close} disabled={acking}
            className="text-[#191614]/40 hover:text-[#191614] transition-colors rounded-full p-1.5 hover:bg-black/5">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 md:px-10 pb-2 min-h-[340px]">
          <AnimatePresence mode="wait">
            <motion.div key={idx} {...stepMotion} className="h-full">
              {step.type === "welcome" && (
                <div className="py-8 text-center max-w-md mx-auto">
                  <div className="mx-auto h-14 w-14 rounded-2xl bg-[#e5342e]/10 text-[#e5342e] flex items-center justify-center mb-5">
                    <Sparkles className="h-7 w-7" strokeWidth={2} />
                  </div>
                  <h2 className="font-heading text-3xl font-black tracking-tight text-[#191614]">Welcome to {data.community_name || `your ${V.lc("org")}`}</h2>
                  <p className="text-[#191614]/60 mt-3 leading-relaxed">
                    You're set up as a <span className="font-semibold text-[#191614]">{roleWord}</span>. Here's a 60-second tour of everything your {V.lc("org")}'s concierge can do — and how you'll stay in the loop.
                  </p>
                </div>
              )}

              {step.type === "whatsnew" && (
                <div className="py-6">
                  <div className="text-center mb-5">
                    <span data-testid="whats-new-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#f59e0b]/10 text-[#f59e0b] text-xs font-black uppercase tracking-widest border border-[#f59e0b]/20 mb-3">
                      <Sparkles className="h-3 w-3" /> New for {data.community_name || `your ${V.lc("org")}`}
                    </span>
                    <h2 className="font-heading text-2xl font-black tracking-tight text-[#191614]">Something new is turned on</h2>
                    <p className="text-[#191614]/60 mt-1.5 text-sm">Here's what was just added to your {V.lc("org")}.</p>
                  </div>
                  <div className="space-y-3">
                    {featuresToShow.map((k) => <FeatureRow key={k} k={k} onGo={goTo} isNew />)}
                  </div>
                </div>
              )}

              {step.type === "features" && (
                <div className="py-6">
                  <h2 className="font-heading text-2xl font-black tracking-tight text-[#191614] mb-1">What your {V.lc("org")} can do</h2>
                  <p className="text-[#191614]/60 text-sm mb-5">These are turned on for {data.community_name || `your ${V.lc("org")}`}. Tap any to jump in.</p>
                  <div className="space-y-3">
                    {featuresToShow.map((k) => <FeatureRow key={k} k={k} onGo={goTo} />)}
                  </div>
                </div>
              )}

              {step.type === "notifications" && (
                <div className="py-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className="h-5 w-5 text-[#e5342e]" strokeWidth={2} />
                    <h2 className="font-heading text-2xl font-black tracking-tight text-[#191614]">How should we reach you?</h2>
                  </div>
                  <p className="text-[#191614]/60 text-sm mb-4">Pick email, text or call for each kind of activity. Text &amp; call need a verified mobile — add one below.</p>
                  <NotificationPrefs onboarding hideDone />
                </div>
              )}

              {step.type === "cadence" && (
                <div className="py-8">
                  <div className="flex items-center gap-2 mb-1">
                    <FileBarChart className="h-5 w-5 text-[#e5342e]" strokeWidth={2} />
                    <h2 className="font-heading text-2xl font-black tracking-tight text-[#191614]">Your {V.lc("org")} report</h2>
                  </div>
                  <p className="text-[#191614]/60 text-sm mb-5">{V.AGrp()}-ready summary — calls, texts, what got resolved and {V.lc("person")} satisfaction — emailed to your managers & {V.grp()} automatically. Choose how often for {data.community_name || `your ${V.lc("org")}`}:</p>
                  <CadenceStep tenantId={tenantId} initial={data.report_frequency} />
                  <p className="text-[11px] text-[#191614]/45 mt-4">You (and each teammate) can fine-tune your own copy anytime from My Account → Notifications.</p>
                </div>
              )}

              {step.type === "done" && (
                <div className="py-10 text-center max-w-md mx-auto">
                  <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-5">
                    <PartyPopper className="h-7 w-7" strokeWidth={2} />
                  </div>
                  <h2 className="font-heading text-3xl font-black tracking-tight text-[#191614]">You're all set</h2>
                  <p className="text-[#191614]/60 mt-3 leading-relaxed">
                    That's the tour. When {data.community_name || `your ${V.lc("org")}`} turns on something new, we'll pop back in with just the new bit. You can replay this anytime from your profile menu.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-[#e7e1da] bg-[#fbfaf9]">
          <div className="flex items-center gap-1">
            {idx > 0 && (
              <button data-testid="wizard-back-button" onClick={back}
                className="inline-flex items-center gap-1.5 rounded-full text-[#191614]/60 hover:text-[#191614] hover:bg-black/5 font-semibold px-5 py-2.5 transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
            {/* "Skip for now" stays available on every step — a first-run modal you can only
                dismiss with a small × in the corner reads like the app is stuck. */}
            {!isLast && (
              <button data-testid="wizard-skip-inline" onClick={close} disabled={acking}
                className="rounded-full text-[#191614]/50 hover:text-[#191614] font-semibold px-5 py-2.5 transition-colors">
                Skip for now
              </button>
            )}
          </div>
          {isLast ? (
            <button data-testid="wizard-finish-button" onClick={close} disabled={acking}
              className="inline-flex items-center gap-2 rounded-full bg-[#e5342e] text-white font-bold px-7 py-2.5 hover:bg-[#E5484D] transition-colors shadow-sm disabled:opacity-60">
              Go to dashboard <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button data-testid="wizard-next-button" onClick={next}
              className="inline-flex items-center gap-2 rounded-full bg-[#191614] text-white font-bold px-7 py-2.5 hover:bg-[#191614]/90 transition-colors shadow-sm">
              Next <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
